
import ConnectionServiceBase, { ConnectionTestResult } from './ConnectionServiceBase';

export interface GraphApiConfig {
  clientId?: string;
  tenantId?: string;
  clientSecret?: string;
  certificate?: string;
  certificatePassword?: string;
  scopes?: string;
  endpoint?: string;
}

/**
 * Service for managing Microsoft Graph API connections
 */
class GraphConnectionService extends ConnectionServiceBase {
  private static instance: GraphConnectionService;
  private config: GraphApiConfig = {};
  private isConfigured: boolean = false;
  
  private constructor() {
    super();
    this.logSource = 'GraphConnectionService';
  }

  public static getInstance(): GraphConnectionService {
    if (!GraphConnectionService.instance) {
      GraphConnectionService.instance = new GraphConnectionService();
    }
    return GraphConnectionService.instance;
  }

  /**
   * Configure Graph API connection settings
   */
  public configure(config: GraphApiConfig): void {
    this.config = { ...config };
    this.isConfigured = !!(this.config.clientId && this.config.tenantId);
    
    this.logInfo('Graph API connection configured', {
      clientId: config.clientId,
      tenantId: config.tenantId,
      hasClientSecret: !!config.clientSecret,
      hasCertificate: !!config.certificate,
      scopes: config.scopes,
      endpoint: config.endpoint,
      isConfigured: this.isConfigured
    });
  }

  /**
   * Check if the service has been configured with valid credentials
   */
  public isProperlyConfigured(): boolean {
    return this.isConfigured;
  }

  /**
   * Test the Graph API connection by calling the edge function
   */
  public async testConnection(): Promise<ConnectionTestResult> {
    // For client credentials flow, we need to use the correct scope format
    const scope = "https://graph.microsoft.com/.default";
    
    this.logInfo('Testing Graph API connection...', { 
      clientId: this.config.clientId,
      tenantId: this.config.tenantId,
      hasClientSecret: !!this.config.clientSecret,
      hasCertificate: !!this.config.certificate,
      scope: scope, // Using the correct scope format for logging
      endpoint: this.config.endpoint
    });
    
    try {
      // Validate required configuration locally first
      if (!this.config.clientId || !this.config.tenantId) {
        const error = 'Missing required configuration: Client ID or Tenant ID';
        this.logWarn(error, this.config);
        return { success: false, error };
      }
      
      // Check if we have either client secret or certificate
      if (!this.config.clientSecret && !this.config.certificate) {
        const error = 'Missing authentication method: Either Client Secret or Certificate is required';
        this.logWarn(error, this.config);
        return { success: false, error };
      }
      
      // Call the Edge Function to test the connection
      this.logDebug('Calling connect-ms-graph edge function...', {
        type: 'graph', 
        operation: 'test',
        scope: scope, // Pass the correct scope to the edge function
        hasConfig: true
      });
      
      const { data, error } = await this.supabase.functions.invoke('connect-ms-graph', {
        body: {
          type: 'graph',
          operation: 'test',
          config: {
            clientId: this.config.clientId,
            tenantId: this.config.tenantId,
            clientSecret: this.config.clientSecret,
            certificate: this.config.certificate,
            certificatePassword: this.config.certificatePassword,
          }
        }
      });
      
      if (error) {
        this.logError('Edge function error', error);
        return { 
          success: false, 
          error: `Edge function error: ${error.message || 'Unknown error'}` 
        };
      }
      
      if (!data) {
        this.logError('No data returned from edge function', null);
        return { 
          success: false, 
          error: 'No data returned from edge function' 
        };
      }
      
      this.logInfo('Edge function response', data);
      
      if (!data.success) {
        const errorMessage = data.error || 'Unknown error';
        const hint = data.hint || '';
        
        this.logError('Graph API connection test failed', {
          error: errorMessage,
          hint: hint,
          raw_error: data.raw_error
        });
        
        return { 
          success: false, 
          error: hint ? `${errorMessage}. ${hint}` : errorMessage,
          details: data.raw_error
        };
      }
      
      // Return success result
      const result = {
        success: true,
        data: data.data
      };
      
      this.logInfo('Graph API connection test successful', result.data);
      return result;
    } catch (error: any) {
      this.logError('Error testing Graph API connection', error);
      return {
        success: false,
        error: `Connection test error: ${error.message || 'Unknown error'}`
      };
    }
  }
}

export default GraphConnectionService.getInstance();
